import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from "../../../components/ui/Input";

const UnifiedUploadSection = ({ onFileSelect, onLinkSubmit, acceptedFormats, maxSize }) => {
  const [activeTab, setActiveTab] = useState('file');
  const [isDragging, setIsDragging] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => { e?.preventDefault(); e?.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e?.preventDefault(); e?.stopPropagation(); setIsDragging(false); };
  const handleDragOver  = (e) => { e?.preventDefault(); e?.stopPropagation(); };

  const handleDrop = (e) => {
    e?.preventDefault(); e?.stopPropagation(); setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files);
    if (files?.length > 0) onFileSelect(files?.[0]);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) onFileSelect(files?.[0]);
  };

  const handleBrowseClick = () => fileInputRef?.current?.click();

  const validateUrl = (url) => {
    try {
      const u = new URL(url);
      return u?.protocol === 'http:' || u?.protocol === 'https:';
    } catch { return false; }
  };

  const handleLinkSubmit = () => {
    setLinkError('');
    if (!linkUrl?.trim()) { setLinkError('Please enter a valid URL'); return; }
    if (!validateUrl(linkUrl)) { setLinkError('Please enter a valid URL (http:// or https://)'); return; }
    onLinkSubmit(linkUrl);
    setLinkUrl('');
  };

  // ── Shared button style — updated gradient ──────────────────
  const GRADIENT_BTN = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '12px 28px', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)',
    color: '#fff', fontWeight: 700, fontSize: 15,
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,74,173,0.35)',
    fontFamily: 'inherit',
    transition: 'opacity 0.2s, transform 0.15s, box-shadow 0.15s',
    width: '100%', justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  };

  const btnHoverOn  = (e) => {
    e.currentTarget.style.opacity   = '0.92';
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,74,173,0.45)';
  };
  const btnHoverOff = (e) => {
    e.currentTarget.style.opacity   = '1';
    e.currentTarget.style.transform = 'none';
    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,74,173,0.35)';
  };
  const btnDown     = (e) => {
    e.currentTarget.style.opacity   = '0.85';
    e.currentTarget.style.transform = 'translateY(0)';
  };
  const btnUp       = (e) => {
    e.currentTarget.style.opacity   = '0.92';
    e.currentTarget.style.transform = 'translateY(-1px)';
  };

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 16,
      overflow: 'hidden',
    }}>

      {/* ── Tab Navigation ── */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
        {[
          { key: 'file', icon: 'Upload', label: 'Upload Deck'   },
          { key: 'link', icon: 'Link',   label: 'Submit a Link' },
        ].map(tab => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '16px 24px',
                fontFamily: 'inherit', fontSize: 14, fontWeight: active ? 700 : 500,
                color: active ? '#004AAD' : '#64748b',
                background: active ? 'rgba(0,74,173,0.04)' : 'transparent',
                borderBottom: active ? '2px solid #004AAD' : '2px solid transparent',
                border: 'none',
                borderBottomStyle: 'solid',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <Icon name={tab.icon} size={16} color={active ? '#004AAD' : '#94a3b8'} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab Content ── */}
      <div style={{ padding: '32px' }}>

        {activeTab === 'file' ? (

          /* ── File Upload ── */
          <div
            style={{
              position: 'relative',
              border: `2px dashed ${isDragging ? '#004AAD' : '#e2e8f0'}`,
              borderRadius: 14,
              background: isDragging ? 'rgba(0,74,173,0.04)' : '#fafbfc',
              transition: 'all 0.2s',
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div style={{ padding: '48px 32px', textAlign: 'center' }}>

              {/* Icon */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: 'rgba(0,74,173,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name="Upload" size={36} color="#004AAD" />
                </div>
              </div>

              {/* Heading */}
              <h3 style={{
                fontFamily: "'Sansation', sans-serif",
                fontSize: 22, fontWeight: 700,
                color: '#0f172a', margin: '0 0 10px',
                letterSpacing: '-0.01em',
              }}>
                Upload Your Presentation Deck
              </h3>

              {/* Subtext */}
              <p style={{
                fontFamily: 'inherit',
                fontSize: 14, color: '#64748b',
                margin: '0 auto 24px', maxWidth: 360,
                lineHeight: 1.6,
              }}>
                Drag and drop your file here, or click to browse
              </p>

              {/* Browse Files Button */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <button
                  onClick={handleBrowseClick}
                  style={{ ...GRADIENT_BTN, width: 'auto' }}
                  onMouseEnter={btnHoverOn}
                  onMouseLeave={btnHoverOff}
                  onMouseDown={btnDown}
                  onMouseUp={btnUp}
                >
                  <Icon name="FolderOpen" size={18} color="#fff" />
                  Browse Files
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFormats}
                onChange={handleFileInput}
                style={{ display: 'none' }}
                aria-label="File upload input"
              />

              {/* Format info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <p style={{ fontFamily: 'inherit', fontSize: 12, color: '#94a3b8', margin: 0 }}>
                  Supported formats: PowerPoint (.pptx), PDF (.pdf)
                </p>
                <p style={{ fontFamily: 'inherit', fontSize: 12, color: '#94a3b8', margin: 0 }}>
                  Maximum file size: {maxSize}MB
                </p>
              </div>

            </div>
          </div>

        ) : (

          /* ── Link Submission ── */
          <div style={{ maxWidth: 560, margin: '0 auto' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 28 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: 'rgba(0,74,173,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon name="Link" size={22} color="#004AAD" />
              </div>
              <div>
                {/* Heading */}
                <h3 style={{
                  fontFamily: "'Sansation', sans-serif",
                  fontSize: 20, fontWeight: 700,
                  color: '#0f172a', margin: '0 0 6px',
                  letterSpacing: '-0.01em',
                }}>
                  Submit a Presentation Link
                </h3>
                <p style={{ fontFamily: 'inherit', fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
                  Paste a link to your Google Slides presentation or any accessible URL
                </p>
              </div>
            </div>

            {/* Input + Submit Button */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Input
                type="url"
                label="Presentation URL"
                placeholder="https://docs.google.com/presentation/d/..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e?.target?.value)}
                error={linkError}
                description="Make sure your presentation is set to 'Anyone with the link can view'"
              />

              <button
                onClick={handleLinkSubmit}
                disabled={!linkUrl?.trim()}
                style={{
                  ...GRADIENT_BTN,
                  opacity: !linkUrl?.trim() ? 0.5 : 1,
                  cursor: !linkUrl?.trim() ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={e => { if (linkUrl?.trim()) btnHoverOn(e); }}
                onMouseLeave={btnHoverOff}
                onMouseDown={e => { if (linkUrl?.trim()) btnDown(e); }}
                onMouseUp={e => { if (linkUrl?.trim()) btnUp(e); }}
              >
                Submit Link
                <Icon name="ExternalLink" size={16} color="#fff" />
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedUploadSection;